import React, { useContext, useState } from 'react';
import { AuthContext } from '../shared/Context/auth-context';
import { useSessionState } from '../shared/hooks/session-state-hook';
import { sessionKey } from '../shared/util/session-storage';
import { useCareMutation } from './care-api';
import { formatCareDate } from './care-utils';
import { reviewersFor } from './EntryComposer';

const EntryDiscussion = ({ entry, record, onSaved }) => {
  const auth = useContext(AuthContext);
  const patient = record.viewer.role === 'patient';
  const assigned = record.viewer.role === 'doctor' && entry.reviewerId === auth.doctorId;
  const actorId = patient
    ? auth.patientId
    : record.viewer.role === 'doctor'
      ? auth.doctorId
      : auth.pharmacyId;
  const [draft, setDraft, clearDraft, stored] = useSessionState(
    sessionKey(
      `${record.viewer.role}:${actorId}`,
      `care:${record.patient.id}:response:${entry.id}`
    ),
    { body: '', action: patient ? 'reply' : 'feedback', reviewerId: '' }
  );
  const { mutate, saving, error } = useCareMutation();
  const [validation, setValidation] = useState(null);
  const path = `/patients/${record.patient.id}/entries/${entry.id}`;
  const reviewers = reviewersFor(record, entry.type);
  const currentReviewer = reviewers.find((member) => member.personId === entry.reviewerId);
  const send = async (event) => {
    event.preventDefault();
    if (!draft.body.trim()) {
      setValidation('Write a response before sending.');
      return;
    }
    setValidation(null);
    try {
      await mutate(`${path}/responses`, 'POST', {
        body: draft.body.trim(),
        action: patient ? 'reply' : draft.action
      });
      clearDraft();
      onSaved(
        patient
          ? 'Your reply was sent to the reviewer.'
          : 'Your feedback was saved for the patient.'
      );
    } catch {}
  };
  const change = async (action) => {
    if (action === 'request-review' && !draft.reviewerId) {
      setValidation('Choose a doctor to review this update.');
      return;
    }
    setValidation(null);
    try {
      await mutate(path, 'PATCH', {
        action,
        ...(action === 'request-review' ? { reviewerId: draft.reviewerId } : {})
      });
      onSaved(
        action === 'acknowledge'
          ? 'You acknowledged this feedback.'
          : 'The selected doctor has been assigned this update.'
      );
    } catch {}
  };
  return (
    <div className="care-discussion">
      <h4>Feedback & next steps</h4>
      {entry.responses?.length ? (
        <ol className="care-thread">
          {entry.responses.map((response) => (
            <li key={response.id}>
              <div className="care-thread-meta">
                <strong>{response.author?.name || 'Care team'}</strong>
                <span>
                  {response.author?.role} · {formatCareDate(response.createdAt)}
                </span>
              </div>
              <p className="care-prewrap">{response.body}</p>
              {response.action === 'appointment' && (
                <span className="care-pill">Consultation requested</span>
              )}
              {response.action === 'request-information' && (
                <span className="care-pill">More information requested</span>
              )}
            </li>
          ))}
        </ol>
      ) : (
        <p className="care-muted">No feedback yet.</p>
      )}
      {entry.acknowledgedAt && (
        <p className="care-muted">
          Patient acknowledged the feedback on {formatCareDate(entry.acknowledgedAt)}. This does not
          confirm completion of the care plan.
        </p>
      )}
      {patient &&
        entry.responses?.some((response) => response.author?.role === 'doctor') &&
        !entry.acknowledgedAt && (
          <button
            className="button button--inverse"
            disabled={saving}
            onClick={() => change('acknowledge')}
          >
            I have read this feedback
          </button>
        )}
      {(assigned || (patient && entry.reviewerId && currentReviewer)) && (
        <form onSubmit={send}>
          <fieldset disabled={saving}>
            {!patient && (
              <label htmlFor={`response-action-${entry.id}`}>
                Next step
                <select
                  id={`response-action-${entry.id}`}
                  value={draft.action}
                  onChange={(event) => setDraft({ ...draft, action: event.target.value })}
                >
                  <option value="feedback">Send feedback</option>
                  <option value="reviewed">Mark reviewed with a note</option>
                  <option value="request-information">Request more information</option>
                  <option value="appointment">Request a consultation</option>
                </select>
              </label>
            )}
            <label htmlFor={`response-body-${entry.id}`}>
              {patient ? 'Reply to your doctor' : 'Feedback for the patient'}
              <textarea
                id={`response-body-${entry.id}`}
                rows={3}
                required
                maxLength={4000}
                value={draft.body}
                onChange={(event) => setDraft({ ...draft, body: event.target.value })}
              />
            </label>
            {!patient && (
              <p className="care-muted">
                For medication changes, issue a prescription through Create prescription so the
                existing prescription checks are applied.
              </p>
            )}
            <button className="button" type="submit" disabled={saving}>
              {saving ? 'Sending…' : patient ? 'Send reply' : 'Send feedback'}
            </button>
          </fieldset>
        </form>
      )}
      {patient && (
        <div className="care-review-assignment">
          {entry.reviewerId && !currentReviewer && (
            <p className="care-notice">
              The previous reviewer no longer has active access. Choose a doctor below to continue
              the review.
            </p>
          )}
          <label htmlFor={`reviewer-${entry.id}`}>
            {entry.reviewerId ? 'Assign this update to another reviewer' : 'Request a review'}
            <select
              id={`reviewer-${entry.id}`}
              value={draft.reviewerId}
              onChange={(event) => setDraft({ ...draft, reviewerId: event.target.value })}
            >
              <option value="">Choose a doctor</option>
              {reviewers.map((member) => (
                <option key={member.personId} value={member.personId}>
                  {member.name}
                </option>
              ))}
            </select>
          </label>
          <p className="care-muted">
            Only this update will be assigned. Changing your primary doctor does not transfer
            outstanding reviews automatically.
          </p>
          <button
            className="button button--inverse"
            type="button"
            disabled={saving || !draft.reviewerId}
            onClick={() => change('request-review')}
          >
            Assign reviewer
          </button>
        </div>
      )}
      {!stored && (
        <p className="care-error" role="status">
          Your response draft could not be kept in this tab. Keep this page open until you send it.
        </p>
      )}
      {(validation || error) && (
        <p className="care-error" role="alert">
          {validation || error}
        </p>
      )}
    </div>
  );
};
export default EntryDiscussion;
